/** Spiegel von apps/api/src/ticket/ticket.regeln.ts — Werte dort gleich halten. */
export type TicketStatus = "offen" | "ruecksprache" | "freigegeben" | "abgelehnt" | "erledigt";
export type TicketPrio = "niedrig" | "normal" | "hoch" | "kritisch";
export type TicketArt = "problem" | "idee";
export type TicketBereich =
  | "dashboard" | "kontakte" | "risiken" | "vertraege" | "kommunikation" | "vorgaenge" | "allgemein"
  // Alt-Werte: bleiben gültig und lesbar, werden aber nicht mehr angeboten.
  | "schaeden" | "telefonie" | "mail" | "kalender" | "einstellungen" | "sonstiges";

export const STATUS_LABEL: Record<TicketStatus, string> = {
  offen: "Offen", ruecksprache: "Rücksprache", freigegeben: "Freigegeben", abgelehnt: "Abgelehnt", erledigt: "Erledigt",
};

/**
 * Die Reiter der Übersicht (User 26.10.2026: „Rücksprache" und „Archiv").
 * „Archiv" ist kein Status, sondern ein Kennzeichen: Archivierte Tickets
 * behalten ihren Status, stehen aber nur noch dort — „Alle" meint alle aktiven.
 */
export type TicketReiter = TicketStatus | "alle" | "archiv";
export const REITER: TicketReiter[] = ["offen", "ruecksprache", "freigegeben", "erledigt", "abgelehnt", "alle", "archiv"];
export const REITER_LABEL: Record<TicketReiter, string> = { ...STATUS_LABEL, alle: "Alle", archiv: "Archiv" };
/** Anzahl je Reiter, wie die API sie liefert (je Status nur aktive, dazu das Archiv). */
export type TicketAnzahl = Record<TicketStatus | "archiv", number>;

/** Zahl am Reiter: „Alle" zählt die aktiven Tickets, nicht das Archiv. */
export function reiterAnzahl(anzahl: TicketAnzahl | undefined, r: TicketReiter): number {
  if (!anzahl) return 0;
  if (r === "alle") return (Object.keys(STATUS_LABEL) as TicketStatus[]).reduce((s, k) => s + (anzahl[k] ?? 0), 0);
  return anzahl[r] ?? 0;
}
export const PRIO_LABEL: Record<TicketPrio, string> = {
  niedrig: "Niedrig", normal: "Normal", hoch: "Hoch", kritisch: "Kritisch",
};
export const ART_LABEL: Record<TicketArt, string> = { problem: "Problem", idee: "Idee" };

/** Kurzes Anzeige-Label für Listen, Chips und Verlauf — für ALLE Werte, auch die alten. */
export const BEREICH_LABEL: Record<TicketBereich, string> = {
  dashboard: "Dashboard", kontakte: "Kontakte", risiken: "Risiken", vertraege: "Verträge",
  kommunikation: "Termine/Schriftverkehr/Telefonie", vorgaenge: "Vorgänge", allgemein: "Allgemein",
  schaeden: "Schäden", telefonie: "Telefonie", mail: "Mail", kalender: "Kalender", einstellungen: "Einstellungen", sonstiges: "Sonstiges",
};

/** Was im Dialog, im Detail und im Filter zur Auswahl steht — in dieser Reihenfolge. */
export const BEREICHE_AUSWAHL: TicketBereich[] = [
  "dashboard", "kontakte", "risiken", "vertraege", "kommunikation", "vorgaenge", "allgemein",
];

/** Label in einer Auswahl. */
export const bereichAuswahlLabel = (b: TicketBereich): string => BEREICH_LABEL[b];

/**
 * Optionen für eine Bereich-Auswahl. Steht das Ticket noch auf einem Alt-Wert
 * (z. B. „Sonstiges“), wird dieser vorne mit angezeigt — sonst zeigte das
 * Select still einen falschen Wert an.
 */
export const bereichOptionen = (aktuell?: TicketBereich | null): TicketBereich[] =>
  aktuell && !BEREICHE_AUSWAHL.includes(aktuell) ? [aktuell, ...BEREICHE_AUSWAHL] : BEREICHE_AUSWAHL;

export type TicketTechnik = {
  browser?: string;
  fenster?: { breite: number; hoehe: number };
  appVersion?: string;
  spuren?: Array<{ art: "konsole" | "api"; zeit: string; text: string }>;
  /** Letzte besuchte Orte vor dem Melden (seitenVerlauf.ts), älteste zuerst. */
  verlauf?: Array<{ zeit: string; route: string; ansicht: string | null }>;
  /** Telefonie-Diagnose (telefonieDiagnose.ts, T-28) — nur, wenn es etwas zu berichten gibt. */
  telefonie?: TicketTelefonie;
};

export type TelefonieSpurStufe = "error" | "warn" | "info" | "log" | "debug";

/** Zustand des Remote-Audios (Spiegel von services/softphoneKeinTon.ts → AudioZustand). */
export type TicketAudioZustand = {
  hatStream: boolean; tracksGesamt: number; tracksLive: number; paused: boolean;
  readyState: number; muted: boolean; sinkId: string | null; playFehler: string | null;
};

/**
 * Telefonie-Stand beim Melden. Der Client liefert SDK-Stand, Anrufe aus Sicht
 * des Softphone-Wrappers, Mikrofon-Freigabe, Geräte-ANZAHL und die
 * [telephony]-Konsolenspuren; `nebenstelle` und `anrufe` (CallLog der letzten
 * 15 Minuten) ergänzt der Server beim Anlegen.
 */
export type TicketTelefonie = {
  erfasstUm?: string;
  /** true, wenn die Sammlung in den Timeout lief (dann fehlen Teile). */
  unvollstaendig?: boolean;
  sdk?: { status: string; fehler: string | null; registriert: boolean | null; aktiverAnruf: string | null } | null;
  softphoneAnrufe?: Array<{
    callId: string; richtung: "inbound" | "outbound"; nummer: string | null; zustand: string;
    start: string; angenommen: string | null; ende: string | null;
    tracksMax: number; tracksLiveMax: number; audioNachAnnahme: TicketAudioZustand | null; keinTon: string | null;
    /** T-233: Klick auf „Annehmen" und was daraus wurde (fehlt bei älteren Tickets). */
    annehmenGeklickt?: string | null; annehmenErgebnis?: string | null; endeGrund?: string | null;
  }>;
  audio?: TicketAudioZustand | null;
  mikrofon?: "granted" | "denied" | "prompt" | "unbekannt";
  geraete?: { eingaenge: number; ausgaenge: number } | null;
  spuren?: Array<{ stufe: TelefonieSpurStufe; zeit: string; text: string }>;
  /** Serverseitig: Nebenstelle des Melders (YeastarExtensionMapping). */
  nebenstelle?: string | null;
  /** Serverseitig: eigene Anrufe (CallLog) der letzten 15 Minuten, neueste zuerst. */
  anrufe?: Array<{
    id: number; callId: string; richtung: string; status: string | null; nummer: string | null;
    start: string; dauerSek: number | null; gespraechSek: number | null;
  }>;
};

export type TicketEntwurf = {
  art: TicketArt;
  betreff: string;
  inhalt: string;
  bereich: TicketBereich;
  route: string | null;
  seitentitel: string | null;
  /** Aktiver Tab / Ansicht / oberstes Fenster beim Melden (ansichtAusDom). */
  ansicht: string | null;
  bezugTyp: string | null;
  bezugId: string | null;
  bezugLabel: string | null;
  technik: TicketTechnik | null;
};

export type TicketListenZeile = {
  id: number; art: TicketArt; betreff: string; status: TicketStatus; prio: TicketPrio | null;
  bereich: TicketBereich; tenantId: string; createdAt: string; letzteAktivitaet: string;
  erstelltVon: string; erstelltVonName: string;
  /** Archiviert (ISO); null = aktiv. */
  archiviertAm: string | null;
};

export type TicketEintrag = {
  id: number; ticketId: number; art: "kommentar" | "ergaenzung" | "status" | "prio" | "bereich" | "archiv";
  text: string | null; alt: string | null; neu: string | null; autor: string; autorName: string; zeitpunkt: string;
};

export type TicketAnhangInfo = {
  id: number; eintragId: number | null; art: "screenshot" | "datei"; dateiname: string;
  mimeType: string | null; groesse: number | null; createdAt: string;
};

export type TicketDetail = TicketListenZeile & {
  inhalt: string | null; route: string | null; seitentitel: string | null; ansicht: string | null;
  bezugTyp: string | null; bezugId: string | null; bezugLabel: string | null;
  technik: TicketTechnik | null; stand: number; quelle: string;
  eintraege: TicketEintrag[]; anhaenge: TicketAnhangInfo[];
};
